<template>
<div class="chapter">
<div class="inner_page">
<h2 class="h2"><a id="Ch01">Chapter One<br/><strong>M<span class="small">ALICE IN</span> W<span class="small">ONDERLAND</span></strong></a></h2><a id="Ch01">
<div class="blockquote">
<p class="noindent"><em>“I don’t want to go among mad people,” Alice remarked. “Oh, you can’t help that,” said the Cat. “We’re all mad here.”</em></p>
<p class="attribution">LEWIS CARROLL</p></div>
<h3 class="h3"><strong>Sign, Sign, Everywhere a Sign</strong></h3>
<p class="noindent">Your Sagittarius boyfriend acts more like a loudmouthed circus clown than a happy-go-lucky charmer. Your Capricorn brother is a social-climbing snob. His Libra lover is shallow, fickle, and spends hours in front of the mirror.</p>
<p class="noindent">You’re confused or skeptical because none of the glowing descriptions in that $1.98 <em>Guide to the Zodiac</em> you thumbed through at the checkout counter match the neurotic bunch you live and work with in the real world.</p>
<p class="noindent">The ancients believed that each of us contains a Universe. We are the center around which the vast threads and everyday minutiae of our life spins. Aries Rodney King asked, “Why can’t we all just get along?” The honest question is, “Why can’t you guys all get along with me?” Forget feel-good astrology and take a trip to the maladjusted side of the Universe. Outwardly, each of us may profess to live for others. Secretly, we all want what we want, right now.</p>
<p class="noindent">Everyone is mad here, just as the Cheshire Cat said; and egotistical, obsessed, controlling, and moody. Knowing how each sign manifests those disagreeable qualities and learning how to control, aim, and fire the force of your own wicked twin within are the keys to staying ahead of that pack of monsters snapping at your heels.</p>
<p class="noindent">The Basics</p>
<p class="noindent">In astrology, there are four elements (Fire, Earth, Air, and Water), three qualities (Cardinal, Fixed, and Mutable), and two polarities (Masculine and Feminine).</p>
<p class="noindent">FIRE signs are <strong>selfish</strong>. Aries, Leo, and Sagittarius are egotistical and demanding. This holier-than-thou bunch thinks the sun rises and sets on them. They attempt to control the rest of the world by force, intimidation, and temper tantrums. These human volcanoes erupt on a regular basis.</p>
<p class="noindent">EARTH signs are <strong>hardheaded</strong>. Taurus, Virgo, and Capricorn are calculating, critical, and callous. They are preoccupied with work and penny-pinching and they point out everyone else’s faults. Boring and churlish, they subscribe to the do-as-I-say not-as-I-do philosophy of control. They dole out their affections as carefully as their money.</p>
<p class="noindent">AIR signs are <strong>flighty</strong>. Gemini, Libra, and Aquarius are fickle, glib, and long-winded. They change lovers, jobs, and their minds as often as the rest of us change underwear. And with just as little thought. Rationalization and justification are Air’s tools of control. They want us to think they are logical. In reality we usually give in just to shut them up.</p>
<p class="noindent">WATER signs are <strong>moody</strong>. Cancer, Scorpio, and Pisces are the drama kings and queens. Self-destructive and manipulative, this awful bunch tries to control the rest of us through emotional blackmail. Water seeks not only its lowest level but yours as well. These characters will stop at nothing to find your weak spot and pick at it until you crack.</p>
<p class="noindent">As for the qualities and polarities, the following comparisons will give you enough to get started.</p>
</a><p class="noindent"><a id="Ch01">Traditional astrology defines <strong>Cardinal</strong> as the initiator and leader. Rotten-truth translation: a bossy, pot-stirring meddler. Aries, Cancer, Capricorn, and Libra are the Cardinal signs.</a><a id="Page_2"></a></p><a id="Page_2">
<p class="noindent">The <strong>Fixed</strong> quality is defined as stable and persistent. Translation: a stubborn, exasperating bore. The Fixed signs are Aquarius, Leo, Scorpio, and Taurus. <strong>Mutable</strong> signs are described as flexible and adaptable. Translation: an inconsistent, self-delusional escapist. Gemini, Sagittarius, Pisces, and Virgo are the Mutable signs.</p>
<p class="noindent">Finally, each sign is assigned a <strong>Masculine</strong> (outgoing) or <strong>Feminine</strong> (receptive) polarity. The rotten truth about this facet of the sun-sign personality is that masculine is an aggressor and feminine is an emotional manipulator.</p>
<p class="noindent">Use the following tables as quick guides to the basic nature of each sign.</p>
<h4 class="h4a">T<span class="small">RADITIONAL</span></h4>
<table cellpadding="0" cellspacing="0" width="100%">
<colgroup><col width="25%"/>
<col width="25%"/>
<col width="25%"/>
<col width="25%"/>
</colgroup><tbody><tr>
<td><strong>Sign</strong></td>
<td><strong>Element</strong></td>
<td><strong>Quality</strong></td>
<td><strong>Polarity</strong></td>
</tr>
<tr><td>Aries</td>
<td>Fire</td>
<td>Cardinal</td>
<td>Masculine</td>
</tr>
<tr>
<td>Taurus</td>
<td>Earth</td>
<td>Fixed</td>
<td>Feminine</td>
</tr>
<tr><td>Gemini</td>
<td>Air</td>
<td>Mutable</td>
<td>Masculine</td>
</tr>
<tr><td>Cancer</td>
<td>Water</td>
<td>Cardinal</td>
<td>Feminine</td>
</tr>
<tr><td>Leo</td>
<td>Fire</td>
<td>Fixed</td>
<td>Masculine</td>
</tr>
<tr><td>Virgo</td>
<td>Earth</td>
<td>Mutable</td>
<td>Feminine</td>
</tr>
<tr><td>Libra</td>
<td>Air</td>
<td>Cardinal</td>
<td>Masculine</td>
</tr>
<tr><td>Scorpio</td>
<td>Water</td>
<td>Fixed</td>
<td>Feminine</td>
</tr>
<tr><td>Sagittarius</td>
<td>Fire</td>
<td>Mutable</td>
<td>Masculine</td>
</tr>
<tr><td>Capricorn</td>
<td>Earth</td>
<td>Cardinal</td>
<td>Feminine</td>
</tr>
<tr><td>Aquarius</td>
<td>Air</td>
<td>Fixed</td>
<td>Masculine</td>
</tr>
<tr><td>Pisces</td>
<td>Water</td>
<td>Mutable</td>
<td>Feminine</td>
</tr></tbody></table>
</a><p class="noindent"><a id="Page_2"></a><a id="Page_3"></a></p><a id="Page_3">
<h4 class="h4a">R<span class="small">OTTEN</span> T<span class="small">RUTH</span></h4>
<table cellpadding="0" cellspacing="0" width="100%">
<colgroup><col width="20%"/>
<col width="20%"/>
<col width="20%"/>
<col width="40%"/>
</colgroup><tbody><tr>
<td><strong>Sign</strong></td>
<td><strong>Element</strong></td>
<td><strong>Quality</strong></td>
<td><strong>Polarity</strong></td></tr>
<tr><td>Aries</td>
<td>Selfish</td>
<td>Bossy</td>
<td>Aggressive</td>
</tr>
<tr><td>Taurus</td>
<td>Hardheaded</td>
<td>Stubborn</td>
<td>Emotional Manipulator</td>
</tr>
<tr><td>Gemini</td>
<td>Flighty</td>
<td>Inconsistent</td>
<td>Aggressive</td>
</tr>
<tr><td>Cancer</td>
<td>Moody</td>
<td>Bossy</td>
<td>Emotional Manipulator</td>
</tr>
<tr><td>Leo</td>
<td>Selfish</td>
<td>Stubborn</td>
<td>Aggressive</td>
</tr>
<tr><td>Virgo</td>
<td>Hardheaded</td>
<td>Inconsistent</td>
<td>Emotional Manipulator</td>
</tr>
<tr><td>Libra</td>
<td>Flighty</td>
<td>Bossy</td>
<td>Aggressive</td>
</tr>
<tr><td>Scorpio</td>
<td>Moody</td>
<td>Stubborn</td>
<td>Emotional Manipulator</td>
</tr>
<tr><td>Sagittarius</td>
<td>Selfish</td>
<td>Inconsistent</td>
<td>Aggressive</td>
</tr>
<tr><td>Capricorn</td>
<td>Hardheaded</td>
<td>Bossy</td>
<td>Emotional Manipulator</td>
</tr>
<tr><td>Aquarius</td>
<td>Flighty</td>
<td>Stubborn</td>
<td>Aggressive</td>
</tr>
<tr><td>Pisces</td>
<td>Moody</td>
<td>Inconsistent</td>
<td>Emotional Manipulator</td>
</tr></tbody></table>
<p class="noindent">How does it work? Let’s look at Aquarius. How can a person be flighty, stubborn, and aggressive? Water Bearers can change horses midstream. Question their decision, and you’ll suffer a tiresome, in-your-face dissertation aimed at forcing your agreement. That’s aggressive. They can have twenty lovers in a year. That’s flighty. The fact that they are determined to keep going until they find true love and dumb enough to think it exists—that’s stubborn.</p>
<h3 class="h3"><strong>Down the Rabbit-Hole</strong></h3>
<p class="noindent">Now that you have the basics, it’s time to take an in-depth look at the sun signs. Each sign has its own chapter, and each chapter is divided into the following subsections:</p>
</a><p class="noindent"><a id="Page_3">• Approach with Caution. Regardless of gender, age, or sexual preference, natives of the individual sun signs share some common bad behaviors.</a><a id="Page_4"></a></p><a id="Page_4">
<p class="noindent">• If You Love One, Man or Woman. Everyone’s on their best behavior at the beginning of the love game. But before you decide to make it permanent, be warned. You could go to sleep with Betty Crocker and wake up with Lizzie Borden. Or fall for a character who color-codes your closet and tapes a lovemaking schedule on the bedroom door.</p>
<p class="noindent">• If You Are One-Born Rotten. Yes, this is all about <em>you.</em> But, it’s not all bad. Anyone reading it will soon discover that you are a force of nature not to be trifled with, in or out of the sack.</p>
<p class="noindent">• It’s All Relative-The Family. Ever wonder why your parents were so weird and your siblings so selfish, whiny, and mean? Find out here and also how to deal with that nutty bunch into which you did not ask to be born.</p>
<p class="noindent">• Office Party-Bitches, Snitches, and the Chronically Inert. Life at the office. Who’s the worst boss (none of them are good). Who’s most likely to stab you in the back, or the front, on the scramble up the corporate ladder. How to handle churlish coworkers, cover your ass, and save your job.</p>
<p class="noindent">• Can’t We All Get Along? Tips for surviving the four worst behaviors of each sign (e.g., throw food to calm a Taurus and a brick to shut up an Aries).</p>
<p class="noindent">• Quick Tips for Emergencies. A 911 list that may save your sanity, or at least buy you enough time to leave town.</p>
<p class="noindent">• A List of the Infamous and Ridiculous. There’s a little bit of each of them in each of us.</p>
</a><p class="noindent"><a id="Page_4">Enjoy your tour and remember, bad behavior isn’t only in our genes—it’s in our stars.</a><a id="Page_5"></a></p><a id="Page_5">
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch01.vue?852e097a